// Import components
const { ProductCalculator } = require("./components/ProductCalculator.js");
const {
  METERS_SQUARED,
  METERS,
  formatCurrency,
} = require("./constants.js");

let vueProduct = vueAppData.product ?? null;

const getProductType = () => {
  if (vueProduct?.packSize) {
    return "pack";
  } else if (vueProduct?.rollWidths) {
    return "roll";
  } else if (vueProduct.productType == "Rug") {
    return vueProduct.productType;
  }
  return "accessory";
};

function createProductStore() {
  const { createStore } = Vuex;

  return createStore({
    state() {
      return {
        /* static data */
        productId: vueProduct?.productId,
        defaultPrice: Number(vueProduct?.productPrice),
        defaultRegularPrice: Number(vueProduct?.productRegularPrice),
        packSize: vueProduct?.packSize ? Number(vueProduct?.packSize) : null,
        rollWidths: vueProduct?.rollWidths ?? null,
        bulkTiers: vueProduct?.bulkTiers ?? null,
        bulkDiscountPrice: vueProduct?.bulkDiscountPrice,
        bulkOrderThreshold: vueProduct?.bulkOrderThreshold,
        productName: vueProduct?.name,
        productType: getProductType(),
        underlayProducts: vueProduct?.underlayProducts ?? null,
        adhesiveProducts: vueProduct?.adhesiveProducts ?? null,
        weldrodProducts: vueProduct?.weldrodProducts ?? null,
        weldrodOptions: vueProduct?.weldrodOptions ?? null,
        freeUpsells: vueProduct?.freeUpsells ?? null,
        roomsEnabled: vueProduct?.roomsEnabled,
        backingEnabled: vueProduct?.backingEnabled,
        hybridEnabled: vueProduct?.hybridEnabled,
        maxAreaMeters: 50000,
        accessoryDescriptionName: vueProduct?.accessory_description_name,

        rugSizes: vueProduct?.rugSizes ?? null,
        selectedSize: null,

        /* variable data */
        area: null,
        width: null,
        length: null,
        quantity: null,
        unit: "m2",
        recommendedWastage: false,
        accessories: [],
        cart: vueAppData.cart,
      };
    },
    mutations: {
      setArea(state, area) {
        if (area > state.maxAreaMeters) {
          area = state.maxAreaMeters;
        }
        state.area = area;
      },
      setLength(state, length) {
        state.length = length;
      },
      setWidth(state, width) {
        state.width = width;
      },
      setUnit(state, unit) {
        state.unit = unit;
      },
      setPacks(state, packs) {
        state.packs = packs;
      },
      addAccessories(state, accessories) {
        accessories.forEach((newAccessory) => {
          if (newAccessory.quantity == 0) return;

          if (newAccessory.accessories_type === "Underlay") {
            state.accessories = state.accessories.filter(
              (acc) => acc.accessories_type !== "Underlay"
            );
          }

          const existingAccessory = state.accessories.find(
            (accessory) => accessory.id === newAccessory.id
          );

          if (existingAccessory) {
            existingAccessory.quantity = newAccessory.quantity;
          } else {
            state.accessories.push(newAccessory);
          }
        });
      },
      updateAccessory(state, { index, quantity }) {
        let accessories = state.accessories;
        accessories[index].quantity = quantity;
      },
      removeAccessory(state, index) {
        state.accessories.splice(index, 1);
      },
      setQuantity(state, quantity) {
        state.quantity = quantity;
      },
      updateRugSize(state, size) {
        state.selectedSize = size;
      },
    },
    actions: {
      setUnit({ commit }, unit) {
        commit("setUnit", unit);
      },
      setArea({ commit }, area) {
        commit("setArea", area);
      },
      setLength({ commit }, length) {
        commit("setLength", length);
      },
      setWidth({ commit }, width) {
        commit("setWidth", width);
      },
      addAccessories({ commit }, accessories) {
        commit("addAccessories", accessories);
      },
      removeAccessory({ commit }, index) {
        commit("removeAccessory", index);
      },
      updateAccessory({ commit }, data) {
        if (data.quantity <= 0) {
          return commit("removeAccessory", data.index);
        }
        commit("updateAccessory", data);
      },
      setPacks({ commit, state }, packs) {
        let area = packs * state.packSize;

        if (state.recommendedWastage) {
          area = area - Number(area * 0.1);
        }

        if (area != 0) {
          area -= 0.02;
        }

        commit("setArea", area.toFixed(2));
        commit("setPacks", packs);
      },
      setQuantity({ commit }, quantity) {
        commit("setQuantity", quantity);
      },
      resetPacks({ commit }) {
        commit("setPacks", null);
      },
    },
    getters: {
      unitText: () => {
        return METERS;
      },
      unitSquaredText: () => {
        return METERS_SQUARED;
      },
      packsNeeded: (state, getters) => {
        if (state.quantity) {
          return state.quantity;
        }
        return Math.ceil(getters.computedArea / state.packSize);
      },
      computedArea: (state) => {
        let area = 0;

        if (state.area) {
          area = state.area < 0 ? -Number(state.area) : Number(state.area);
        } else if (state.productType === "roll" && state.width != null && state.length != null) {
          // Carpet/roll: derive area from width × length when area not yet set
          const w = Number(state.width);
          const l = Number(state.length);
          if (w > 0 && l > 0) area = w * l;
        }

        if (area > 0 && state.recommendedWastage) {
          area += Number(area * 0.1);
        }

        return area;
      },
      totalComputedArea: (state, getters) => {
        let area = 0;

        if (state.productType == "pack") {
          area = getters.packsNeeded * state.packSize;
        } else if (state.productType == "roll") {
          area = getters.computedArea;
        }

        return area || 0; // Ensure we always return a number
      },
      showWastageOption: (state, getters) => {
        if (state.productType !== "pack" || !state.area || state.area <= 0) {
          return false;
        }
        const orderedArea = getters.totalComputedArea;
        const requiredArea = Number(state.area);
        const effectiveWastage = (orderedArea - requiredArea) / requiredArea;
        return effectiveWastage < 0.1;
      },
      hasBuiltInWastage: (state, getters) => {
        if (state.productType !== "pack" || !state.area || state.area <= 0) {
          return false;
        }
        const orderedArea = getters.totalComputedArea;
        const requiredArea = Number(state.area);
        const effectiveWastage = (orderedArea - requiredArea) / requiredArea;
        return effectiveWastage >= 0.1;
      },
      isDiscounted: (state, getters) => {
        return getters.productPrice !== state.defaultPrice;
      },
      productPrice: (state, getters) => {
        let price = state.defaultPrice;
        let appliedTierIndex = -1;

        const allTiers = document.querySelectorAll("div[data-tier-index]");
        allTiers.forEach((tier) => {
          const tierBoxes = tier.querySelectorAll(".tier-box");
          if (tierBoxes && tierBoxes.length) {
            tierBoxes.forEach((box) => box.classList.remove("active-tier"));
          }
        });

        if (state.bulkTiers) {
          const highestDiscount = state.bulkTiers.reduce(
            (maxDiscount, tier, index) => {
              const bulkDiscountQty = Number(tier.bulk_discount_qty);
              const bulkDiscountPercent = Number(tier["bulk_discount_%"]);
              let quantity =
                state.productType == "pack" || state.productType == "accessory"
                  ? getters.packsNeeded
                  : getters.computedArea;

              if (
                quantity >= bulkDiscountQty &&
                bulkDiscountPercent > maxDiscount
              ) {
                appliedTierIndex = index;
                return bulkDiscountPercent;
              }
              return maxDiscount;
            },
            0
          );

          if (highestDiscount > 0) {
            price -= price * (highestDiscount / 100);
          } else {
            appliedTierIndex = -1;
          }
        } else if (
          state.bulkDiscountPrice &&
          state.length >= state.bulkOrderThreshold
        ) {
          price = state.bulkDiscountPrice;
          appliedTierIndex = 0;
        } else {
          appliedTierIndex = -1;
        }

        const activeTierElements = document.querySelectorAll(
          `div[data-tier-index="${appliedTierIndex}"] .tier-box`
        );
        if (activeTierElements && activeTierElements.length) {
          activeTierElements.forEach((el) => el.classList.add("active-tier"));
        }

        if (state.selectedSize) {
          price = Number(
            state.selectedSize.rug_sale_price ||
              state.selectedSize.rug_default_price
          );
        }

        return price;
      },
      pricePerM2: (state, getters) => {
        let price = getters.productPrice;

        if (state.productType == "pack") {
          price = price / state.packSize;
        }

        return price;
      },
      regularPricePerM2: (state, getters) => {
        if (state.productType == "pack") {
          return state.defaultRegularPrice / state.packSize;
        }
        return state.defaultRegularPrice;
      },
      computedTotal: (state, getters) => {
        let price = 0;

        if (state.productType == "pack") {
          price = getters.productPrice * getters.packsNeeded;
        } else if (state.productType == "roll") {
          price = getters.productPrice * getters.computedArea;
        }

        if (state.accessories.length > 0) {
          state.accessories.forEach((accessory) => {
            price += accessory.quantity * accessory.price;
          });
        }

        const priceDisplay = document.querySelector(
          "#productPriceDisplay .product-price"
        );
        if (priceDisplay) {
          const perM2Text = (state.productType === "pack" || state.productType === "roll") 
            ? " <span class='text-base'>per m²</span>" 
            : "";
          priceDisplay.innerHTML =
            formatCurrency(getters.pricePerM2) + perM2Text;
        }

        return price;
      },
      isFinanceEligible: (state, getters) => {
        const total = getters.computedTotal;
        return total >= 750 && total <= 10000;
      },
      availableFinanceTerms: (state, getters) => {
        const total = getters.computedTotal;
        if (total >= 3000 && total <= 10000) {
          return [6, 8, 10];
        } else if (total >= 1500 && total < 3000) {
          return [6, 8];
        } else if (total >= 750 && total < 1500) {
          return [6];
        }
        return [];
      },
      maxFinanceTerm: (state, getters) => {
        const terms = getters.availableFinanceTerms;
        return terms.length > 0 ? Math.max(...terms) : 0;
      },
      minMonthlyPayment: (state, getters) => {
        const total = getters.computedTotal;
        const maxTerm = getters.maxFinanceTerm;
        if (maxTerm === 0) return 0;

        const depositAmount = total * 0.1; // 10% minimum deposit
        const financeAmount = total - depositAmount;
        return financeAmount / maxTerm;
      },
    },
  });
}

function initProductCalculator() {
  const { createApp } = Vue;

  const productStore = createProductStore();

  const app = createApp(ProductCalculator);
  app.use(productStore);

  // Mount the app if element exists
  const mountElement = document.getElementById("product-calculator-app");
  if (mountElement) {
    app.mount(mountElement);
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", initProductCalculator);
